/**
 * SignalCard Component
 *
 * Displays a single trading signal with action buttons
 * Memoized for performance optimization
 */

import { memo } from 'react';
import { getSignalColor, type TradingSignal } from '../../hooks/useTradingSignals';

interface SignalCardProps {
  signal: TradingSignal;
  onOpenDetails: (signal: TradingSignal) => void;
  onRunBacktest: (tokenName: string, currentPrice: number, tokenId?: string) => void;
  onExplainSignal: (signal: TradingSignal) => void;
  backendAvailable: boolean;
  backtestLoading: boolean;
  explanationLoading: boolean;
}

/**
 * Trading Signal Card Component
 * Memoized to prevent unnecessary re-renders
 */
export const SignalCard = memo(function SignalCard({
  signal,
  onOpenDetails,
  onRunBacktest,
  onExplainSignal,
  backendAvailable,
  backtestLoading,
  explanationLoading
}: SignalCardProps) {
  const actionColor = getSignalColor(signal.action);

  return (
    <div
      style={{
        padding: '16px',
        background: 'var(--bg)',
        borderRadius: '12px',
        borderLeft: `4px solid ${actionColor}`,
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '16px',
        alignItems: 'center'
      }}
    >
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>{signal.tokenName}</div>
          <div
            style={{
              padding: '4px 12px',
              borderRadius: '6px',
              background: actionColor,
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase'
            }}
          >
            {signal.action}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
            Confidence: <strong>{signal.confidence.toFixed(0)}%</strong>
          </div>
        </div>

        <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '4px' }}>
          {signal.reason}
        </div>

        <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
          Current: ${signal.currentPrice.toLocaleString()} • Timeframe: {signal.timeframe}
          {signal.suggestedPrice && ` • Entry: $${signal.suggestedPrice.toLocaleString()}`}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
        <button
          onClick={() => onOpenDetails(signal)}
          className="btn-ghost"
          style={{ padding: '8px 16px', fontSize: '13px' }}
        >
          Details
        </button>
        <button
          onClick={() => onRunBacktest(signal.tokenName, signal.currentPrice, signal.tokenId)}
          disabled={backtestLoading || !backendAvailable}
          className="btn-ghost"
          style={{
            padding: '8px 16px',
            fontSize: '13px',
            background: backendAvailable ? 'rgba(139, 92, 246, 0.1)' : 'rgba(100, 100, 100, 0.1)',
            border: `1px solid ${backendAvailable ? 'rgba(139, 92, 246, 0.3)' : 'rgba(100, 100, 100, 0.3)'}`,
            color: backendAvailable ? 'var(--accent)' : 'var(--muted)',
            opacity: backtestLoading ? 0.6 : 1,
            cursor: backtestLoading || !backendAvailable ? 'not-allowed' : 'pointer'
          }}
          title={!backendAvailable ? 'Backend not available' : 'Run backtest for this token'}
        >
          {backtestLoading ? '⏳ Running...' : '🔬 Run Backtest'}
        </button>
        <button
          onClick={() => onExplainSignal(signal)}
          disabled={explanationLoading || !backendAvailable}
          className="btn-ghost"
          style={{
            padding: '8px 16px',
            fontSize: '13px',
            background: backendAvailable ? 'rgba(59, 130, 246, 0.1)' : 'rgba(100, 100, 100, 0.1)',
            border: `1px solid ${backendAvailable ? 'rgba(59, 130, 246, 0.3)' : 'rgba(100, 100, 100, 0.3)'}`,
            color: backendAvailable ? '#3b82f6' : 'var(--muted)',
            opacity: explanationLoading ? 0.6 : 1,
            cursor: explanationLoading || !backendAvailable ? 'not-allowed' : 'pointer'
          }}
          title={!backendAvailable ? 'Backend not available' : 'Get AI explanation for this signal'}
        >
          {explanationLoading ? '⏳ Loading...' : '🧠 Explain Signal'}
        </button>
      </div>
    </div>
  );
});

SignalCard.displayName = 'SignalCard';
